import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { X, DollarSign, FileText, Plus, Trash2 } from 'lucide-react';
import { createContract, fetchContractParties } from '../../store/slices/contractsSlice';
import { projectService } from '../../services/projectService';
import type { RootState, AppDispatch } from '../../store';
import type { CreateContractData } from '../../types/contracts';
import type { Project } from '../../types';
import LoadingSpinner from '../LoadingSpinner';
import DatePicker from '../forms/DatePicker';

interface ContractFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialProjectId?: string;
}

const ContractForm: React.FC<ContractFormProps> = ({ isOpen, onClose, initialProjectId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, contractParties, partiesLoading } = useSelector(
    (state: RootState) => state.contracts
  );

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [milestones, setMilestones] = useState([
    { title: '', amount: 0, dueDate: '', description: '' },
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateContractData>({
    defaultValues: {
      projectId: initialProjectId || '',
      currency: 'USD',
      requiresApproval: false,
    },
  });

  const _watchedProjectId = watch('projectId');

  useEffect(() => {
    if (isOpen) {
      // Fetch projects
      const loadProjects = async () => {
        if (projects.length === 0) {
          setProjectsLoading(true);
          try {
            const response = await projectService.getProjects({ page: 1, limit: 100 });
            if (response.success && response.data?.projects) {
              setProjects(response.data.projects);
            }
          } catch (error) {
            console.error('Failed to fetch projects:', error);
          } finally {
            setProjectsLoading(false);
          }
        }
      };

      loadProjects();

      // Fetch contract parties
      dispatch(fetchContractParties({ page: 1, limit: 100 }));
    }
  }, [dispatch, isOpen, projects.length]);

  useEffect(() => {
    if (initialProjectId) {
      setValue('projectId', initialProjectId);
    }
  }, [initialProjectId, setValue]);

  const onSubmit = async (data: CreateContractData) => {
    try {
      // Add milestones to the contract data
      const contractData = {
        ...data,
        milestones: milestones
          .filter(m => m.title.trim() !== '')
          .map(m => ({
            title: m.title,
            amount: Number(m.amount),
            dueDate: new Date(m.dueDate),
            description: m.description,
            status: 'pending' as const,
          })),
      };

      await dispatch(createContract(contractData)).unwrap();
      onClose();
      reset();
      setMilestones([{ title: '', amount: 0, dueDate: '', description: '' }]);
    } catch (error) {
      console.error('Failed to create contract:', error);
    }
  };

  const addMilestone = () => {
    setMilestones([...milestones, { title: '', amount: 0, dueDate: '', description: '' }]);
  };

  const removeMilestone = (index: number) => {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((_, i) => i !== index));
    }
  };

  const updateMilestone = (index: number, field: string, value: any) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], [field]: value };
    setMilestones(updated);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-6 py-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <FileText className="w-6 h-6 text-brand-orange" />
                <h2 className="text-2xl font-bold text-gray-900">Create New Contract</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Title *
                  </label>
                  <input
                    {...register('title', { required: 'Contract title is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    placeholder="Enter contract title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="projectId"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Project *
                  </label>
                  {projectsLoading ? (
                    <div className="flex items-center justify-center py-2">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : (
                    <select
                      {...register('projectId', { required: 'Project selection is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    >
                      <option value="">Select a project</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.projectId && (
                    <p className="text-red-500 text-xs mt-1">{errors.projectId.message}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="contractTypeId"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Contract Type *
                  </label>
                  <select
                    {...register('contractTypeId', { required: 'Contract type is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                  >
                    <option value="">Select contract type</option>
                    <option value="construction">Construction Contract</option>
                    <option value="service">Service Agreement</option>
                    <option value="supply">Supply Contract</option>
                    <option value="maintenance">Maintenance Contract</option>
                    <option value="consulting">Consulting Agreement</option>
                  </select>
                  {errors.contractTypeId && (
                    <p className="text-red-500 text-xs mt-1">{errors.contractTypeId.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="partyId" className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Party *
                  </label>
                  {partiesLoading ? (
                    <div className="flex items-center justify-center py-2">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : (
                    <select
                      {...register('partyId', { required: 'Contract party is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    >
                      <option value="">Select contract party</option>
                      {contractParties.map(party => (
                        <option key={party.id} value={party.id}>
                          {party.name} ({party.type})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.partyId && (
                    <p className="text-red-500 text-xs mt-1">{errors.partyId.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Amount *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      {...register('amount', {
                        required: 'Contract amount is required',
                        min: { value: 0, message: 'Amount must be positive' },
                      })}
                      type="number"
                      step="0.01"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                      placeholder="0.00"
                    />
                  </div>
                  {errors.amount && (
                    <p className="text-red-500 text-xs mt-1">{errors.amount.message}</p>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input
                    {...register('startDate', { required: 'Start date is required' })}
                    type="hidden"
                  />
                  <DatePicker
                    label="Start Date *"
                    value={watch('startDate') || null}
                    onChange={(date) => setValue('startDate', date || '', { shouldValidate: true })}
                    error={errors.startDate?.message}
                    required
                  />
                </div>

                <div>
                  <input
                    {...register('endDate', { required: 'End date is required' })}
                    type="hidden"
                  />
                  <DatePicker
                    label="End Date *"
                    value={watch('endDate') || null}
                    onChange={(date) => setValue('endDate', date || '', { shouldValidate: true })}
                    error={errors.endDate?.message}
                    required
                  />
                </div>
              </div>

              {/* Description and Scope */}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    placeholder="Brief description of the contract"
                  />
                </div>

                <div>
                  <label htmlFor="scope" className="block text-sm font-medium text-gray-700 mb-1">
                    Scope of Work *
                  </label>
                  <textarea
                    {...register('scope', { required: 'Scope of work is required' })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    placeholder="Detailed description of work to be performed"
                  />
                  {errors.scope && (
                    <p className="text-red-500 text-xs mt-1">{errors.scope.message}</p>
                  )}
                </div>
              </div>

              {/* Milestones */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Contract Milestones</h3>
                  <button
                    type="button"
                    onClick={addMilestone}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-brand-orange text-white rounded-lg hover:bg-brand-orange-dark transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Milestone</span>
                  </button>
                </div>

                {milestones.map((milestone, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Milestone {index + 1}</h4>
                      {milestones.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMilestone(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Milestone title"
                          value={milestone.title}
                          onChange={e => updateMilestone(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          placeholder="Amount"
                          value={milestone.amount}
                          onChange={e =>
                            updateMilestone(index, 'amount', parseFloat(e.target.value) || 0)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                        />
                      </div>
                      <div>
                        <DatePicker
                          value={milestone.dueDate || null}
                          onChange={(date) => updateMilestone(index, 'dueDate', date || '')}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <textarea
                          placeholder="Milestone description"
                          value={milestone.description}
                          onChange={e => updateMilestone(index, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    {...register('requiresApproval')}
                    type="checkbox"
                    className="w-4 h-4 text-brand-orange focus:ring-brand-orange border-gray-300 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Requires approval workflow
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white bg-brand-orange border border-transparent rounded-lg hover:bg-brand-orange-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50"
                >
                  {loading && <LoadingSpinner size="sm" />}
                  <span>{loading ? 'Creating...' : 'Create Contract'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractForm;
